// Infix expressions for Max Markup: {dep.guests > 0 && !paid ? "Maksu avoinna" : ""}. They
// compile to the JSONLogic-style expressions in expr.ts (the only form that is stored or
// evaluated) and print back from them. Nothing here evaluates anything.
import { EXPR_BODY_ARGS, EXPR_LAMBDA_OPS, EXPR_OPS, EXPR_SCOPES, validLoopName } from '../expr.ts'

export class MarkupError extends Error {
  readonly line: number
  readonly col: number
  constructor (message: string, line: number, col: number) {
    super(`${line}:${col} ${message}`)
    this.line = line
    this.col = col
  }
}

/** Where an expression starts in the source, so errors point at the right place */
export interface SourcePos { line: number, col: number }

/** Names a bare identifier may stand for, innermost first */
export interface NameScope {
  /** repeat/list/timeline as names in effect: dep.start_ts means loop.dep.start_ts */
  loops: string[]
  /** component params: room means props.room */
  params: string[]
  derived: string[]
  state: string[]
}

export const EMPTY_SCOPE: NameScope = { loops: [], params: [], derived: [], state: [] }

const SCOPES = new Set<string>(EXPR_SCOPES)
const KEYWORDS = new Set(['true', 'false', 'null'])
/** Operators written as functions; the symbolic ones are operators in the grammar */
export const FUNCTIONS = new Set<string>(EXPR_OPS.filter(op => /^[a-z]/i.test(op) && op !== 'var'))
/** Function names for operators that are symbols in the grammar, for forms the infix cannot write (a < x < b) */
const ALIASES: Record<string, string> = { len: 'length', obj: 'object', not: '!', lt: '<', lte: '<=', gt: '>', gte: '>=', add: '+', mul: '*' }
const FUNCTION_NAME: Record<string, string> = { '<': 'lt', '<=': 'lte', '>': 'gt', '>=': 'gte', '+': 'add', '*': 'mul' }
/** Operators whose second argument is evaluated once per element with item and index in scope */
export const LAMBDA_OPS = new Set(EXPR_LAMBDA_OPS)

// ---------------------------------------------------------------------------
// Lexer

type Tok =
  | { t: 'num', v: number, at: number }
  | { t: 'str', v: string, at: number }
  | { t: 'id', v: string, at: number }
  | { t: 'op', v: string, at: number }
  | { t: 'end', at: number }

const OPS = ['===', '!==', '==', '!=', '<=', '>=', '&&', '||', '??', '<', '>', '+', '-', '*', '/', '%', '!', '?', ':', '.', ',', '(', ')', '[', ']']

function lex (src: string, pos: SourcePos): Tok[] {
  const out: Tok[] = []
  let i = 0
  const fail = (msg: string, at: number): never => { throw errorAt(src, pos, at, msg) }
  while (i < src.length) {
    const c = src[i]!
    if (/\s/.test(c)) { i++; continue }
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src[i + 1] ?? ''))) {
      const m = /^(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?/.exec(src.slice(i))!
      out.push({ t: 'num', v: Number(m[0]), at: i })
      i += m[0].length
      continue
    }
    if (c === '"' || c === "'") {
      let j = i + 1
      let v = ''
      while (j < src.length && src[j] !== c) {
        if (src[j] === '\\') {
          const n = src[j + 1]
          if (n === undefined) fail('unfinished escape', j)
          v += n === 'n' ? '\n' : n === 't' ? '\t' : n
          j += 2
        } else {
          v += src[j]
          j++
        }
      }
      if (j >= src.length) fail('unterminated string', i)
      out.push({ t: 'str', v, at: i })
      i = j + 1
      continue
    }
    if (/[A-Za-z_]/.test(c)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!
      out.push({ t: 'id', v: m[0], at: i })
      i += m[0].length
      continue
    }
    const op = OPS.find(o => src.startsWith(o, i))
    if (!op) fail(`unexpected character ${JSON.stringify(c)}`, i)
    out.push({ t: 'op', v: op!, at: i })
    i += op!.length
  }
  out.push({ t: 'end', at: src.length })
  return out
}

function errorAt (src: string, pos: SourcePos, at: number, message: string): MarkupError {
  const before = src.slice(0, at)
  const lines = before.split('\n')
  const line = pos.line + lines.length - 1
  const col = lines.length > 1 ? lines[lines.length - 1]!.length + 1 : pos.col + at
  return new MarkupError(message, line, col)
}

// ---------------------------------------------------------------------------
// Parser

type Ast =
  | { k: 'lit', v: string | number | boolean | null, at: number }
  | { k: 'list', items: Ast[], at: number }
  | { k: 'name', v: string, at: number }
  | { k: 'member', obj: Ast, key: string | number | Ast, at: number }
  | { k: 'call', name: string, args: Ast[], at: number }
  | { k: 'unary', op: string, arg: Ast, at: number }
  | { k: 'bin', op: string, a: Ast, b: Ast, at: number }
  | { k: 'cond', test: Ast, then: Ast, else: Ast, at: number }

function parse (src: string, pos: SourcePos): Ast {
  const toks = lex(src, pos)
  let p = 0
  const peek = () => toks[p]!
  const fail = (msg: string, tok: Tok = peek()): never => { throw errorAt(src, pos, tok.at, msg) }
  const isOp = (v: string) => { const t = peek(); return t.t === 'op' && t.v === v }
  const eat = (v: string) => { if (!isOp(v)) fail(`expected ${v}`); p++ }

  const binary = (next: () => Ast, ops: string[]): Ast => {
    let left = next()
    for (;;) {
      const t = peek()
      if (t.t !== 'op' || !ops.includes(t.v)) return left
      p++
      left = { k: 'bin', op: t.v, a: left, b: next(), at: t.at }
    }
  }

  const expr = (): Ast => {
    const test = nullish()
    if (!isOp('?')) return test
    const at = peek().at
    p++
    const then = expr()
    eat(':')
    return { k: 'cond', test, then, else: expr(), at }
  }
  const nullish = () => binary(or, ['??'])
  const or = () => binary(and, ['||'])
  const and = () => binary(eq, ['&&'])
  const eq = () => binary(cmp, ['==', '!=', '===', '!=='])
  const cmp = () => binary(add, ['<', '<=', '>', '>='])
  const add = () => binary(mul, ['+', '-'])
  const mul = () => binary(unary, ['*', '/', '%'])
  const unary = (): Ast => {
    const t = peek()
    if (t.t === 'op' && (t.v === '!' || t.v === '-')) {
      p++
      return { k: 'unary', op: t.v, arg: unary(), at: t.at }
    }
    return postfix()
  }
  const args = (): Ast[] => {
    const out: Ast[] = []
    if (isOp(')')) { p++; return out }
    for (;;) {
      out.push(expr())
      if (isOp(',')) { p++; continue }
      eat(')')
      return out
    }
  }
  const postfix = (): Ast => {
    let node = primary()
    for (;;) {
      const t = peek()
      if (t.t === 'op' && t.v === '.') {
        p++
        const key = peek()
        if (key.t === 'id') { p++; node = { k: 'member', obj: node, key: key.v, at: key.at }; continue }
        if (key.t === 'num' && Number.isInteger(key.v)) { p++; node = { k: 'member', obj: node, key: key.v, at: key.at }; continue }
        fail('expected a field name after .')
      }
      if (t.t === 'op' && t.v === '[') {
        p++
        const inner = expr()
        eat(']')
        node = { k: 'member', obj: node, key: inner.k === 'lit' && (typeof inner.v === 'number' || typeof inner.v === 'string') ? inner.v : inner, at: t.at }
        continue
      }
      if (t.t === 'op' && t.v === '(') {
        if (node.k !== 'name') fail('only named functions can be called')
        p++
        node = { k: 'call', name: (node as { v: string }).v, args: args(), at: node.at }
        continue
      }
      return node
    }
  }
  const primary = (): Ast => {
    const t = peek()
    p++
    switch (t.t) {
      case 'num': return { k: 'lit', v: t.v, at: t.at }
      case 'str': return { k: 'lit', v: t.v, at: t.at }
      case 'id':
        if (t.v === 'true' || t.v === 'false') return { k: 'lit', v: t.v === 'true', at: t.at }
        if (t.v === 'null') return { k: 'lit', v: null, at: t.at }
        return { k: 'name', v: t.v, at: t.at }
      case 'op':
        if (t.v === '(') { const e = expr(); eat(')'); return e }
        if (t.v === '[') {
          const items: Ast[] = []
          if (isOp(']')) { p++; return { k: 'list', items, at: t.at } }
          for (;;) {
            items.push(expr())
            if (isOp(',')) { p++; continue }
            eat(']')
            return { k: 'list', items, at: t.at }
          }
        }
    }
    p--
    return fail(t.t === 'end' ? 'expression ended early' : `unexpected ${t.t === 'op' ? t.v : (t as { v: unknown }).v}`)
  }

  const out = expr()
  if (peek().t !== 'end') fail(`unexpected ${String((peek() as { v?: unknown }).v ?? 'text')} after the expression`)
  return out
}

// ---------------------------------------------------------------------------
// Compile to JSONLogic

const IDENT = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/

/** Resolves the first segment of a bare name to a scope path */
function resolveName (name: string, scope: NameScope, fail: (msg: string) => never): string {
  if (SCOPES.has(name)) return name
  if (scope.loops.includes(name)) return `loop.${name}`
  if (scope.params.includes(name)) return `props.${name}`
  const derived = scope.derived.includes(name)
  const state = scope.state.includes(name)
  if (derived && state) fail(`${name} is both a derive and a state; write derived.${name} or state.${name}`)
  if (derived) return `derived.${name}`
  if (state) return `state.${name}`
  return fail(`unknown name ${name}: not a state, derive, loop name, component param or scope (${[...SCOPES].join(', ')})`)
}

/** a.b[0].c as path segments when it is a plain path, else null */
function pathOf (ast: Ast): (string | number)[] | null {
  if (ast.k === 'name') return [ast.v]
  if (ast.k === 'member' && (typeof ast.key === 'string' || typeof ast.key === 'number')) {
    const base = pathOf(ast.obj)
    if (!base) return null
    if (typeof ast.key === 'string' && !IDENT.test(ast.key)) return null
    if (typeof ast.key === 'number' && (!Number.isInteger(ast.key) || ast.key < 0)) return null
    return [...base, ast.key]
  }
  return null
}

const isStringy = (e: unknown) => typeof e === 'string' || (!!e && typeof e === 'object' && !Array.isArray(e) && 'cat' in e)

function args1 (args: unknown[]): unknown {
  return args.length === 1 && !Array.isArray(args[0]) ? args[0] : args
}

function flatten (op: string, a: unknown, b: unknown): unknown {
  const parts = (e: unknown) => e && typeof e === 'object' && !Array.isArray(e) && op in e && Array.isArray((e as Record<string, unknown>)[op])
    ? (e as Record<string, unknown[]>)[op]!
    : [e]
  return { [op]: [...parts(a), ...parts(b)] }
}

export function compileExpr (src: string, scope: NameScope = EMPTY_SCOPE, pos: SourcePos = { line: 1, col: 1 }): unknown {
  if (!src.trim()) throw new MarkupError('empty expression', pos.line, pos.col)
  return compileAst(parse(src, pos), src, scope, pos)
}

function compileAst (ast: Ast, src: string, scope: NameScope, pos: SourcePos): unknown {
  const fail = (msg: string, at = ast.at): never => { throw errorAt(src, pos, at, msg) }
  const c = (a: Ast) => compileAst(a, src, scope, pos)
  switch (ast.k) {
    case 'lit': return ast.v
    case 'list': return ast.items.map(c)
    case 'name':
    case 'member': {
      const path = pathOf(ast)
      if (path) {
        const [head, ...rest] = path
        const resolved = resolveName(String(head), scope, msg => fail(msg))
        const full = [resolved, ...rest].join('.')
        if (full.split('.').length > 13) fail('path is too long')
        return { var: full }
      }
      if (ast.k === 'name') return fail('unexpected name')
      return { get: [c(ast.obj), typeof ast.key === 'object' ? c(ast.key) : ast.key] }
    }
    case 'call': {
      const name = ALIASES[ast.name] ?? ast.name
      if (name === 'if') {
        if (ast.args.length < 2) fail('if needs a condition and a value')
        return { if: ast.args.map(c) }
      }
      if (name === 'default') {
        const path = ast.args.length === 2 ? compileAst(ast.args[0]!, src, scope, pos) : null
        if (!path || typeof path !== 'object' || !('var' in path) || typeof (path as { var: unknown }).var !== 'string') fail('default(path, value) takes a path and its fallback')
        return { var: [(path as { var: string }).var, c(ast.args[1]!)] }
      }
      if (!FUNCTIONS.has(name) && !(name in FUNCTION_NAME) && name !== '!') fail(`unknown function ${ast.name}; see the expression operators`)
      // map(guides, map(days, obj("gid", g.id, "day", item)), "g"): the name is a loop name in the body
      const at = EXPR_BODY_ARGS[name]
      const named = at ? ast.args[at.name] : undefined
      let bodyScope = scope
      if (named) {
        if (named.k !== 'lit' || !validLoopName(named.v)) fail(`the last argument of ${name} names its body: a string such as "guide" that is not a scope name`, named.at)
        bodyScope = { ...scope, loops: [...scope.loops, (named as { v: string }).v] }
      }
      const args = ast.args.map((a, i) => at && i === at.body ? compileAst(a, src, bodyScope, pos) : c(a))
      if (name === 'today' || name === 'now') {
        if (args.length) fail(`${name}() takes no arguments`)
        return { [name]: [] }
      }
      return { [name]: args1(args) }
    }
    case 'unary': {
      if (ast.op === '-' && ast.arg.k === 'lit' && typeof ast.arg.v === 'number') return -ast.arg.v
      if (ast.op === '!' && ast.arg.k === 'unary' && ast.arg.op === '!') return { '!!': c(ast.arg.arg) }
      return { [ast.op]: c(ast.arg) }
    }
    case 'bin': {
      const a = c(ast.a)
      const b = c(ast.b)
      switch (ast.op) {
        case '??': return flatten('coalesce', a, b)
        case '||': return flatten('or', a, b)
        case '&&': return flatten('and', a, b)
        case '===': return { '==': [a, b] }
        case '!==': return { '!=': [a, b] }
        case '+': return isStringy(a) || isStringy(b) ? flatten('cat', a, b) : flatten('+', a, b)
        case '*': return flatten('*', a, b)
        default: return { [ast.op]: [a, b] }
      }
    }
    case 'cond': {
      const then = c(ast.then)
      const otherwise = c(ast.else)
      // a ? b : c ? d : e is one if with several branches
      if (otherwise && typeof otherwise === 'object' && !Array.isArray(otherwise) && 'if' in otherwise && Array.isArray(otherwise.if) && otherwise.if.length % 2 === 1) {
        return { if: [c(ast.test), then, ...otherwise.if] }
      }
      return { if: [c(ast.test), then, otherwise] }
    }
  }
}

// ---------------------------------------------------------------------------
// Print JSONLogic as infix

const PREC: Record<string, number> = {
  cond: 1, coalesce: 2, or: 3, and: 4, '==': 5, '!=': 5, '<': 6, '<=': 6, '>': 6, '>=': 6, '+': 7, '-': 7, cat: 7, '*': 8, '/': 8, '%': 8, unary: 9, atom: 10
}
const INFIX: Record<string, string> = { coalesce: '??', or: '||', and: '&&', '==': '==', '!=': '!=', '<': '<', '<=': '<=', '>': '>', '>=': '>=', '+': '+', '-': '-', '*': '*', '/': '/', '%': '%' }

function quote (s: string): string {
  return '"' + s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\t/g, '\\t') + '"'
}

/** Shortest name for a var path that resolves back to the same path in this scope */
export function shortPath (path: string, scope: NameScope): string {
  const [head, name, ...rest] = path.split('.')
  if (name === undefined || /^\d/.test(name) || SCOPES.has(name) || KEYWORDS.has(name)) return path
  const short = [name, ...rest].join('.')
  try {
    const resolved = resolveName(name, scope, msg => { throw new Error(msg) })
    return resolved === `${head}.${name}` ? short : path
  } catch {
    return path
  }
}

export function printExpr (e: unknown, scope: NameScope = EMPTY_SCOPE): string {
  return print(e, scope).text
}

function print (e: unknown, scope: NameScope): { text: string, prec: number } {
  const atom = (text: string) => ({ text, prec: PREC.atom! })
  if (e === null) return atom('null')
  if (typeof e === 'boolean') return atom(String(e))
  if (typeof e === 'number') return e < 0 ? { text: String(e), prec: PREC.unary! } : atom(String(e))
  if (typeof e === 'string') return atom(quote(e))
  if (Array.isArray(e)) return atom('[' + e.map(x => print(x, scope).text).join(', ') + ']')
  if (typeof e !== 'object') return atom('null')
  const [op] = Object.keys(e)
  const raw = (e as Record<string, unknown>)[op!]
  const args = Array.isArray(raw) ? raw : [raw]
  const wrap = (x: unknown, min: number) => {
    const r = print(x, scope)
    return r.prec < min ? `(${r.text})` : r.text
  }
  if (op === 'var') {
    const path = String(Array.isArray(raw) ? raw[0] : raw)
    const short = shortPath(path, scope).replace(/\.(\d+)(?=\.|$)/g, '[$1]')
    if (Array.isArray(raw) && raw.length > 1) return atom(`default(${short}, ${print(raw[1], scope).text})`)
    return atom(short)
  }
  if (op === 'if') {
    if (args.length < 2) return atom(`if(${args.map(a => print(a, scope).text).join(', ')})`)
    let text = ''
    let i = 0
    for (; i + 1 < args.length; i += 2) text += `${wrap(args[i], PREC.coalesce!)} ? ${wrap(args[i + 1], PREC.coalesce!)} : `
    text += i < args.length ? wrap(args[i], PREC.cond!) : 'null'
    return { text, prec: PREC.cond! }
  }
  if (op === '!' || op === '!!') {
    const inner = wrap(args[0], PREC.unary!)
    return { text: (op === '!!' ? '!!' : '!') + inner, prec: PREC.unary! }
  }
  if (op === '-' && args.length === 1) return { text: '-' + wrap(args[0], PREC.unary!), prec: PREC.unary! }
  const infix = INFIX[op!]
  const comparable = op === '==' || op === '!=' || op === '<' || op === '<=' || op === '>' || op === '>=' || op === '-' || op === '/' || op === '%'
  if (infix && args.length >= 2 && (!comparable || args.length === 2)) {
    const prec = PREC[op!]!
    // Left-associative: the right operand of - / % and comparisons needs a tighter binding
    const text = args.map((a, i) => wrap(a, i === 0 ? prec : (comparable ? prec + 1 : prec + 1))).join(` ${infix} `)
    if (op === '+' && args.some(isStringy)) return atom(`add(${args.map(a => print(a, scope).text).join(', ')})`)
    return { text, prec }
  }
  if (op === 'cat' && args.length >= 2 && args.some(a => typeof a === 'string')) {
    return { text: args.map(a => wrap(a, PREC.cat! + 1)).join(' + '), prec: PREC.cat! }
  }
  const name = FUNCTION_NAME[op!] ?? op!
  if (name === 'today' || name === 'now') return atom(`${name}()`)
  const at = EXPR_BODY_ARGS[op!]
  const named = at ? args[at.name] : undefined
  const bodyScope = at && validLoopName(named) ? { ...scope, loops: [...scope.loops, named] } : scope
  return atom(`${name}(${args.map((a, i) => print(a, at && i === at.body ? bodyScope : scope).text).join(', ')})`)
}
